import { SportEvent } from '../../ses/types/SportEventTypes';
import { StatKey } from '../types/PlayerShiftTypes';
import { attributeEventStats } from '../../ses/sports/registry';

/**
 * Convert nanosecond timestamp (number, string, or Long-like object) to milliseconds.
 * SSE events carry wall-clock times as nanoseconds since epoch.
 */
export function nsToMs(ns: any): number {
  if (ns == null) return 0;

  // Long-like object from gRPC: { low, high, unsigned }
  if (typeof ns === 'object' && 'low' in ns && 'high' in ns) {
    const low = ns.low >>> 0;
    const high = ns.high >>> 0;
    const bigVal = BigInt(high) * BigInt(0x100000000) + BigInt(low);
    return Number(bigVal / BigInt(1_000_000));
  }

  const n = typeof ns === 'string' ? Number(ns) : ns;
  if (n > 1e15) {
    // Nanoseconds → milliseconds
    return Math.floor(n / 1_000_000);
  }
  // Already milliseconds
  return n;
}

/**
 * Format seconds as "M:SS" game clock display (e.g., 520 → "8:40").
 */
export function formatGameClock(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format total seconds as "MM:SS" time-on-ice (e.g., 1202 → "20:02").
 */
export function formatTOI(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Extract the player ID (main) from a participant-like object.
 * SSE payloads use snake_case: { id: { main: "8047" }, name: { firstname, lastname } }
 */
function getParticipantId(participant: any): string | null {
  if (!participant?.id) return null;
  const main = participant.id.main;
  if (main == null) return null;
  return String(main);
}

/**
 * Which players a given event credits, and toward which stat. Sport-agnostic:
 * the per-sport StatDefinitions (ses/sports/<sport>/stats.ts) own the rules;
 * this just drops the per-stat `value` since the filter only needs (player,
 * stat). Used to match events against an active stat filter.
 */
export function getEventPlayerAttributions(
  event: SportEvent,
): Array<{ playerId: string; stat: StatKey }> {
  return attributeEventStats(event).map(({ playerId, stat }) => ({ playerId, stat }));
}

/**
 * Check if a player (by ID) is involved in a given event.
 * Used to populate the events list inside a shift card.
 */
export function isPlayerInvolvedInEvent(event: SportEvent, playerId: string): boolean {
  const payload = event.payload as any;
  if (!payload?.type || !payload?.data) return false;

  const data = payload.data;

  switch (payload.type) {
    case 'goal':
      return (
        getParticipantId(data.shooter) === playerId ||
        getParticipantId(data.assister) === playerId
      );
    case 'hit':
      return (
        getParticipantId(data.hitter) === playerId ||
        getParticipantId(data.was_hit) === playerId
      );
    case 'shot_on_goal':
    case 'shot':
    case 'missed_shot':
    case 'blocked_shot':
      return getParticipantId(data.shooter) === playerId;
    case 'penalty':
      return getParticipantId(data.committed) === playerId;
    case 'faceoff':
      return getParticipantId(data.possession) === playerId;
    case 'giveaway':
      return (
        getParticipantId(data.committed) === playerId ||
        getParticipantId(data.caused) === playerId
      );

    // ── NBA ────────────────────────────────────────────────────────────
    // Basketball participants carry `id.main` in the same shape as hockey,
    // so getParticipantId matches against the shift's player id directly.
    // Baskets / free throws name the player as `shooter`; everything else
    // (rebounds, fouls, turnovers, violations) uses `player`. Team-attributed
    // events (team rebound, shot-clock turnover) have no player → no match.
    case 'basket_made_2pt':
    case 'basket_made_3pt':
    case 'basket_missed_2pt':
    case 'basket_missed_3pt':
    case 'free_throw_made':
    case 'free_throw_missed':
      return getParticipantId(data.shooter) === playerId;
    case 'rebound_offensive':
    case 'rebound_defensive':
    case 'foul_personal':
    case 'foul_offensive':
    case 'foul_drawn':
    case 'turnover':
    case 'violation':
      return getParticipantId(data.player) === playerId;

    default:
      return false;
  }
}

/** Event types that are meaningful to show inside a shift card. */
const SHIFT_RELEVANT_EVENT_TYPES = new Set([
  // NHL
  'goal',
  'hit',
  'shot_on_goal',
  'missed_shot',
  'blocked_shot',
  'penalty',
  'faceoff',
  'giveaway',
  // NBA
  'basket_made_2pt',
  'basket_made_3pt',
  'basket_missed_2pt',
  'basket_missed_3pt',
  'free_throw_made',
  'free_throw_missed',
  'rebound_offensive',
  'rebound_defensive',
  'foul_personal',
  'foul_offensive',
  'foul_drawn',
  'turnover',
  'violation',
]);

/**
 * Check if an event type is relevant for display inside a shift card.
 */
export function isShiftRelevantEvent(event: SportEvent): boolean {
  const payload = event.payload as any;
  return payload?.type != null && SHIFT_RELEVANT_EVENT_TYPES.has(payload.type);
}
